import { graphql, type ResultOf, type VariablesOf } from '@newfront-insurance/graphql-frontend';

const NOVA_SCHEDULE_FIELDS = graphql(`
  fragment NovaScheduleManagementFields on NovaSchedule @_unmask {
    uuid
    ownerUserUuid
    accountUuid
    sourceChatUuid
    targetChatUuid
    title
    prompt
    attachmentFileUuids
    attachmentFiles {
      uuid
      filename
    }
    timezone
    startAt
    recurrence {
      preset
      minuteOfHour
      timeOfDay
      weekdays
      weekInterval
      dayOfMonth
    }
    status {
      status
      statusReason
      statusDate
    }
    nextRunAt
    lastRunAt
    outputPolicy
    createdAt
    updatedAt
    account {
      uuid
      name
    }
    targetChat {
      uuid
      title
    }
  }
`);

export const GET_NOVA_SCHEDULES = graphql(
  `
    query GetNovaSchedules($filters: NovaScheduleFiltersInput!) {
      getNovaSchedules(filters: $filters) {
        ...NovaScheduleManagementFields
      }
    }
  `,
  [NOVA_SCHEDULE_FIELDS],
);

export const GET_NOVA_SCHEDULE_RUNS = graphql(`
  query GetNovaScheduleRuns($filters: NovaScheduleRunFiltersInput!) {
    getNovaScheduleRuns(filters: $filters) {
      uuid
      scheduleUuid
      occurrenceAt
      chatUuid
      messageUuid
      status
      statusReason
      startedAt
      completedAt
      errorMessage
      createdAt
    }
  }
`);

export const UPDATE_NOVA_SCHEDULE = graphql(
  `
    mutation UpdateNovaSchedule($uuid: String!, $command: UpdateNovaScheduleInput!) {
      updateNovaSchedule(uuid: $uuid, command: $command) {
        ...NovaScheduleManagementFields
      }
    }
  `,
  [NOVA_SCHEDULE_FIELDS],
);

export const PAUSE_NOVA_SCHEDULE = graphql(
  `
    mutation PauseNovaSchedule($uuid: String!) {
      pauseNovaSchedule(uuid: $uuid) {
        ...NovaScheduleManagementFields
      }
    }
  `,
  [NOVA_SCHEDULE_FIELDS],
);

export const RESUME_NOVA_SCHEDULE = graphql(
  `
    mutation ResumeNovaSchedule($uuid: String!) {
      resumeNovaSchedule(uuid: $uuid) {
        ...NovaScheduleManagementFields
      }
    }
  `,
  [NOVA_SCHEDULE_FIELDS],
);

export const ARCHIVE_NOVA_SCHEDULE = graphql(
  `
    mutation ArchiveNovaSchedule($uuid: String!) {
      archiveNovaSchedule(uuid: $uuid) {
        ...NovaScheduleManagementFields
      }
    }
  `,
  [NOVA_SCHEDULE_FIELDS],
);

export const RUN_NOVA_SCHEDULE_NOW = graphql(`
  mutation RunNovaScheduleNow($uuid: String!) {
    runNovaScheduleNow(uuid: $uuid) {
      uuid
      scheduleUuid
      occurrenceAt
      chatUuid
      messageUuid
      status
      statusReason
      startedAt
      completedAt
      errorMessage
      createdAt
    }
  }
`);

type QueriedNovaScheduleRecord = ResultOf<typeof GET_NOVA_SCHEDULES>['getNovaSchedules'][number];

export type NovaScheduleRecord = QueriedNovaScheduleRecord & { uuid: string };
export type NovaScheduleRunRecord = ResultOf<typeof GET_NOVA_SCHEDULE_RUNS>['getNovaScheduleRuns'][number];
export type NovaScheduleRunStatus = NovaScheduleRunRecord['status'];
export type UpdateNovaScheduleCommand = VariablesOf<typeof UPDATE_NOVA_SCHEDULE>['command'];

export function isNovaScheduleRecord(schedule: QueriedNovaScheduleRecord): schedule is NovaScheduleRecord {
  return typeof schedule.uuid === 'string';
}
